import type { ReactNode } from 'react'

import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/libs/utils'

import { isExcludedColumnKey } from './columnConventions'

export interface CardField<T> {
  key: string
  label?: string
  render: (item: T) => ReactNode
}

interface CardGridProps<T> {
  data: T[]
  keyExtractor: (item: T) => string
  onCardClick?: (item: T) => void
  icon?: (item: T) => ReactNode
  title?: (item: T) => ReactNode
  subtitle?: (item: T) => ReactNode
  badge?: (item: T) => ReactNode
  footer?: (item: T) => ReactNode
  /**
   * Field-driven card body. When provided, the card renders a label/value
   * pair for each field whose key is in `visibleFieldKeys` (or every field
   * when `visibleFieldKeys` is undefined). `title` is still used for the
   * card heading; `subtitle`/`footer` are ignored in field mode.
   */
  fields?: CardField<T>[]
  visibleFieldKeys?: string[]
  emptyMessage?: string
  columns?: 1 | 2 | 3 | 4
  className?: string
}

const gridColsMap = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
}

export function CardGrid<T>({
  data,
  keyExtractor,
  onCardClick,
  icon,
  title,
  subtitle,
  badge,
  footer,
  fields,
  visibleFieldKeys,
  emptyMessage = 'No data available',
  columns = 3,
  className,
}: CardGridProps<T>) {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        {emptyMessage}
      </div>
    )
  }

  const visibleFields = fields
    ? (visibleFieldKeys ? fields.filter((f) => visibleFieldKeys.includes(f.key)) : fields).filter(
        (f) => !isExcludedColumnKey(f.key),
      )
    : null

  return (
    <div className={cn('grid gap-4', gridColsMap[columns], className)}>
      {data.map((item, index) => (
        <Card
          key={keyExtractor(item)}
          className={cn(
            'hover:shadow-md transition-all animate-scale-in',
            onCardClick && 'cursor-pointer',
          )}
          style={{ animationDelay: `${index * 50}ms` }}
          onClick={() => onCardClick?.(item)}
        >
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                {title && (
                  <h3 className="font-semibold text-card-foreground truncate">{title(item)}</h3>
                )}
                {badge && <div className="mt-1">{badge(item)}</div>}
              </div>
              {icon && <div className="ml-3 shrink-0">{icon(item)}</div>}
            </div>
            {visibleFields ? (
              <>
                <dl className="space-y-2 text-sm">
                  {visibleFields.map((f) => (
                    <div key={f.key} className="flex items-start justify-between gap-3">
                      {f.label && (
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground shrink-0">
                          {f.label}
                        </dt>
                      )}
                      <dd
                        className={cn(
                          'text-card-foreground min-w-0',
                          f.label ? 'text-right' : 'w-full',
                        )}
                      >
                        {f.render(item)}
                      </dd>
                    </div>
                  ))}
                </dl>
                {footer && <div className="pt-4 mt-4 border-t border-border">{footer(item)}</div>}
              </>
            ) : (
              <>
                {subtitle && (
                  <div className="text-sm text-muted-foreground mb-4">{subtitle(item)}</div>
                )}
                {footer && <div className="pt-4 border-t border-border">{footer(item)}</div>}
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
